import { useState, type ReactElement } from "react";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuPopup,
  MenuTrigger,
  toastManager,
} from "@salestrak/ui";

import {
  UpdateCycleRows,
  type UpdateCyclePick,
} from "#app/components/updates/update-cycle-rows";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import {
  normalizeUpdateCycle,
  type WorkflowCycle,
} from "#app/lib/project-updates";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/** The rungs the editor writes and names. Every workflow fragment selects
 *  `updateCycle`, so any workflow row in the app satisfies this. `name` is
 *  nullable in the schema; the group label drops its suffix without one. */
type EditableWorkflow = WorkflowCycle & {
  workflowId: string;
  name: string | null;
};

// The Workflow's default update cycle, edited from its settings page — the
// bottom rung of the ladder every unpinned Stage inherits. Null = updates off
// workflow-wide (admin-gated, same permission as pinning a Stage off). Shares
// the Stage pin menu's rows: the current default checked, a pick commits at
// once; no inherit row (there is no level below a Workflow) and no override
// note (nothing at any level resets per-Project cycles — Stages and Projects
// with their own simply don't follow the default, and the Stage pin menu
// already counts those).
export function WorkflowUpdateCycleMenu({
  workflow,
  children,
}: {
  workflow: EditableWorkflow | null;
  /** The trigger element. */
  children: ReactElement;
}) {
  const { updateWorkflow } = useWorkflowMutations();
  const canTurnOff = usePermission("manage.stage_updates_off");
  const [open, setOpen] = useState(false);

  if (!workflow) return null;

  const storedCycle = normalizeUpdateCycle(workflow.updateCycle);
  const value: UpdateCyclePick = storedCycle ?? "off";
  // Turning the default OFF is admin-territory; an already-off Workflow keeps
  // the off row checked and live for everyone — picking a cycle turns updates
  // back ON.
  const offLocked = !canTurnOff && storedCycle != null;

  const handlePick = async (pick: UpdateCyclePick) => {
    // No inherit row is rendered, so no inherit pick arrives; the guard keeps
    // the union total.
    const cycle = pick === "off" || pick === "inherit" ? null : pick;
    try {
      const result = await updateWorkflow(workflow.workflowId, {
        updateCycle: cycle,
      });
      if (result && "message" in result) {
        toastManager.add({
          title: result.message ?? "Failed to save default cycle",
          type: "error",
        });
        return;
      }
    } catch (error) {
      toastMutationError(error, "Failed to save default cycle");
    }
  };

  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger render={children} />
      <MenuPopup align="end" width="panel">
        <MenuGroup>
          <MenuGroupLabel>
            Default follow up cycle{workflow.name ? ` · ${workflow.name}` : ""}
          </MenuGroupLabel>
          <UpdateCycleRows
            value={value}
            onPick={(pick) => void handlePick(pick)}
            storedCycle={storedCycle}
            offLocked={offLocked}
          />
        </MenuGroup>
      </MenuPopup>
    </Menu>
  );
}
